import axios from 'axios';
import type { User } from '../auth/types';
import type { CreateUserDto, UpdateUserDto } from './types';

/**
 * Base URL del API
 *
 * @remarks
 * Toma la URL del archivo .env o usa localhost:8080 por defecto
 */
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

/**
 * Instancia de axios configurada con interceptors para autenticación
 */
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Interceptor para agregar el token JWT a todas las peticiones
api.interceptors.request.use(config => {
  // Obtener token del localStorage (donde Zustand lo persiste)
  const authStorage = localStorage.getItem('auth-storage');
  if (authStorage) {
    try {
      const parsed = JSON.parse(authStorage) as { state?: { token?: string } };
      const token = parsed.state?.token;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error parsing auth storage:', error);
    }
  }
  return config;
});

/**
 * API de usuarios para admins
 *
 * @remarks
 * Endpoints para CRUD de usuarios del tenant.
 * Solo accesibles para usuarios con rol 'admin'.
 * Superadmins pueden gestionar usuarios de cualquier tenant.
 *
 * @example
 * ```typescript
 * // Listar usuarios del tenant
 * const users = await usersApi.list();
 *
 * // Crear nuevo usuario
 * const newUser = await usersApi.create({
 *   email: 'user@example.com',
 *   password: 'SecurePass123',
 *   fullName: 'Juan Pérez',
 *   roleName: 'user_medium'
 * });
 *
 * // Actualizar usuario
 * const updatedUser = await usersApi.update('user-id', {
 *   roleName: 'user_high',
 *   isActive: true
 * });
 *
 * // Eliminar usuario
 * await usersApi.delete('user-id');
 * ```
 */
export const usersApi = {
  /**
   * Lista todos los usuarios del tenant del admin autenticado
   *
   * @param tenantId - (Opcional) ID del tenant para filtrar usuarios (solo superadmin)
   * @returns Array de usuarios
   * @throws {AxiosError} Si no está autenticado o no tiene permisos
   */
  async list(tenantId?: string): Promise<User[]> {
    const params = tenantId ? { tenantId } : {};
    const response = await api.get<User[]>('/auth/admin/users', { params });
    return response.data;
  },

  /**
   * Obtiene un usuario específico por ID
   *
   * @param id - ID del usuario
   * @returns Usuario encontrado
   * @throws {AxiosError} Si no existe o no tiene permisos
   */
  async get(id: string): Promise<User> {
    const response = await api.get<User>(`/auth/admin/users/${id}`);
    return response.data;
  },

  /**
   * Crea un nuevo usuario
   *
   * @param data - Datos del nuevo usuario
   * @returns Usuario creado
   * @throws {AxiosError} Si el email ya existe o datos inválidos
   *
   * @remarks
   * - Si no se especifica roleName, usa 'user_low' por defecto
   * - Solo superadmin puede crear admins
   * - El usuario pertenecerá al tenant del admin creador
   */
  async create(data: CreateUserDto): Promise<User> {
    const response = await api.post<User>('/auth/admin/users', data);
    return response.data;
  },

  /**
   * Actualiza un usuario existente
   *
   * @param id - ID del usuario a actualizar
   * @param data - Datos a actualizar (todos opcionales)
   * @returns Usuario actualizado
   * @throws {AxiosError} Si no existe, datos inválidos, o intenta modificarse a sí mismo
   *
   * @remarks
   * - Admin no puede modificar su propio usuario
   * - Solo superadmin puede asignar rol 'admin'
   * - Si se cambia la contraseña, se hashea automáticamente en el backend
   */
  async update(id: string, data: UpdateUserDto): Promise<User> {
    const response = await api.patch<User>(`/auth/admin/users/${id}`, data);
    return response.data;
  },

  /**
   * Elimina un usuario
   *
   * @param id - ID del usuario a eliminar
   * @returns Mensaje de confirmación
   * @throws {AxiosError} Si no existe, no tiene permisos, o intenta eliminarse a sí mismo
   *
   * @remarks
   * - Admin no puede eliminarse a sí mismo
   * - La eliminación es permanente (hard delete)
   */
  async delete(id: string): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>(`/auth/admin/users/${id}`);
    return response.data;
  },
};
